import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { toastService } from "@miplatform/toast";
import { createUUID, sortMaterialResponse } from "@miplatform/utils";
import { arrayMove } from "@dnd-kit/sortable";

export const usePlanAreaViewModel = ({
  area,
  setOpenItemTemplateModal,
  setItemTemplateLabel,
  planDetails,
  itemTemplateLabel,
  stateManager,
  handleCreateSpecPlan,
}) => {
  const [materials, setMaterials] = useState([]);

  const GET_MATERIALS_UUID = createUUID();
  const HANDLE_CREATE_ITEM_TEMPLATE_UUID = createUUID();

  const getMaterials = () => {
    stateManager.abort(GET_MATERIALS_UUID);
    stateManager.run(async () => {
      const planAreaMaterialId =
        area?.materials?.map((m) => m.materialId) ?? [];
      const response = await homeService.materials.getAll({
        filters: { id: { in: { type: "guid", value: planAreaMaterialId } } },
      });
      if (response.status === HTTP_STATUSES.OK) {
        const sortedData = sortMaterialResponse(response.data);
        setMaterials(sortedData);
      }
    }, GET_MATERIALS_UUID);
  };

  const handleMoveUpArea = (index) => {
    const currentArea = planDetails.planAreas[index];
    const nextArea = planDetails.planAreas[index - 1];
    // Prevent moving an area to a position above another area on a higher floor
    if (index > 0 && currentArea.floor > nextArea.floor) {
      return;
    }
    // Prevent moving an area with specColumn value 1 above one with specColumn value 0
    if (
      index > 0 &&
      currentArea.specColumn === 1 &&
      nextArea.specColumn === 0
    ) {
      return;
    }
    if (index > 0) {
      const updatedPlanAreas = arrayMove(
        planDetails.planAreas,
        index,
        index - 1
      );

      const updatedPlanDetails = {
        ...planDetails,
        planAreas: updatedPlanAreas.map((area, idx) => ({
          ...area,
          displayOrder: idx + 1,
        })),
      };
      handleCreateSpecPlan(updatedPlanDetails);
    }
  };

  const handleMoveDownArea = (index) => {
    const currentArea = planDetails.planAreas[index];
    const nextArea = planDetails.planAreas[index + 1];
    if (
      index < planDetails.planAreas.length - 1 &&
      currentArea.floor < nextArea.floor
    ) {
      return;
    }
    if (
      index < planDetails.planAreas.length - 1 &&
      currentArea.specColumn === 0 &&
      nextArea.specColumn === 1
    ) {
      return;
    }
    if (index < planDetails.planAreas.length - 1) {
      const updatedPlanAreas = arrayMove(
        planDetails.planAreas,
        index,
        index + 1
      );
      const updatedPlanDetails = {
        ...planDetails,
        planAreas: updatedPlanAreas.map((area, idx) => ({
          ...area,
          displayOrder: idx + 1,
        })),
      };
      handleCreateSpecPlan(updatedPlanDetails);
    }
  };

  const handleCloseItemTemplateModal = () => {
    setItemTemplateLabel("");
    setOpenItemTemplateModal(false);
  };

  const handleCreateItemTemplate = () => {
    const modifiedData = {
      ...planDetails,
      id: undefined,
      planAreas: [
        {
          ...area,
          id: undefined,
          label: itemTemplateLabel,
          materials: area.materials?.map((material) => ({
            ...material,
            id: undefined,
          })),
        },
      ],
    };
    stateManager.abort(HANDLE_CREATE_ITEM_TEMPLATE_UUID);
    stateManager.run(async () => {
      const response = await homeService.plans.itemTemplates.create({
        data: modifiedData,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOpenItemTemplateModal(false);
        setItemTemplateLabel("");
        toastService.success({
          text: `Successfully created Item Template: ${modifiedData.planAreas[0].label}.`,
        });
      }
    }, HANDLE_CREATE_ITEM_TEMPLATE_UUID);
  };

  return {
    materials,
    setMaterials,
    getMaterials,
    handleMoveUpArea,
    handleMoveDownArea,
    handleCreateItemTemplate,
    handleCloseItemTemplateModal,
  };
};
